import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { publicOnboardingApi, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  CheckCircle, IdentificationCard, Car, User, EnvelopeSimple, Camera, Upload, Trash, ArrowLeft, ArrowRight, Warning,
} from '@phosphor-icons/react';

const STEPS = [
  { key: 'type', label: 'Kundentyp', icon: User },
  { key: 'personal', label: 'Persönliche Daten', icon: User },
  { key: 'address', label: 'Adresse & Kontakt', icon: EnvelopeSimple },
  { key: 'documents', label: 'Ausweisdaten', icon: IdentificationCard },
  { key: 'uploads', label: 'Fotos hochladen', icon: Camera },
  { key: 'review', label: 'Prüfen & Absenden', icon: CheckCircle },
];

const DOC_TYPES = [
  { key: 'id_front', label: 'Personalausweis · Vorderseite' },
  { key: 'id_back', label: 'Personalausweis · Rückseite' },
  { key: 'dl_front', label: 'Führerschein · Vorderseite' },
  { key: 'dl_back', label: 'Führerschein · Rückseite' },
];

const LICENSE_CLASSES = ['AM', 'A1', 'A2', 'A', 'B', 'BE', 'B96', 'C1', 'C1E', 'C', 'CE', 'D1', 'D1E', 'D', 'DE', 'L', 'T'];
const LEGAL_FORMS = ['GmbH', 'UG (haftungsbeschränkt)', 'AG', 'e.K.', 'GbR', 'KG', 'OHG', 'Einzelunternehmen', 'Sonstige'];

const emptyForm = {
  customer_type: 'private',
  salutation: '',
  first_name: '',
  last_name: '',
  date_of_birth: '',
  birth_place: '',
  address: '',
  postal_code: '',
  city: '',
  country: 'Deutschland',
  phone: '',
  email: '',
  id_card_number: '',
  id_card_expiry: '',
  driver_license_number: '',
  driver_license_expiry: '',
  driver_license_authority: '',
  driver_license_classes: [],
  company_name: '',
  company_legal_form: '',
  tax_number: '',
  vat_id: '',
  register_number: '',
  register_court: '',
  notes: '',
};

const PublicOnboarding = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [ob, setOb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const inputRefs = useRef({});
  const cameraRefs = useRef({});

  useEffect(() => {
    const load = async () => {
      try {
        const res = await publicOnboardingApi.get(token);
        setOb(res.data);
        setForm({ ...emptyForm, ...(res.data.form || {}) });
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const isReadOnly = ob && ob.status !== 'pending';

  const saveForm = async (patch, showToast = false) => {
    if (isReadOnly) return;
    setSaving(true);
    try {
      const merged = { ...form, ...patch };
      setForm(merged);
      // Leere String-Felder ausfiltern, sonst validiert Pydantic z.B. EmailStr='' als ungültig
      const clean = {};
      for (const [k, v] of Object.entries(merged)) {
        if (v === '' || v === undefined) continue;
        clean[k] = v;
      }
      const res = await publicOnboardingApi.update(token, clean);
      setOb(res.data);
      if (showToast) toast.success('Gespeichert');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const doUpload = async (docType, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Datei zu groß (max 15 MB)');
      return;
    }
    setUploading((s) => ({ ...s, [docType]: true }));
    try {
      await publicOnboardingApi.upload(token, docType, file);
      const res = await publicOnboardingApi.get(token);
      setOb(res.data);
      toast.success('Foto hochgeladen');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading((s) => ({ ...s, [docType]: false }));
    }
  };

  const deleteDoc = async (docType) => {
    if (!window.confirm('Foto wirklich löschen?')) return;
    try {
      await publicOnboardingApi.deleteDoc(token, docType);
      const res = await publicOnboardingApi.get(token);
      setOb(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      // Erst die letzten Änderungen speichern (leere Strings filtern)
      const clean = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === '' || v === undefined) continue;
        clean[k] = v;
      }
      const res = await publicOnboardingApi.update(token, clean);
      setOb(res.data);
      const s = await publicOnboardingApi.submit(token);
      setOb(s.data);
      toast.success('Anfrage erfolgreich eingereicht!');
      setStep(STEPS.length); // Success view
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA]">
        <p className="text-[#525866]">Lädt …</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA] p-4">
        <div className="max-w-md w-full card-swiss p-8 text-center" data-testid="onboarding-error">
          <Warning size={48} weight="thin" className="mx-auto text-[#E60000]" />
          <h2 className="text-xl font-bold text-[#111418] mt-3">Link ungültig oder abgelaufen</h2>
          <p className="text-sm text-[#525866] mt-2">{error}</p>
          <Button className="btn-primary mt-6" onClick={() => navigate('/registrieren')}>
            Neue Registrierung starten
          </Button>
        </div>
      </div>
    );
  }

  // Success screen (nach Submit oder wenn schon submitted/approved)
  if (step >= STEPS.length || (ob && ob.status !== 'pending')) {
    const isApproved = ob?.status === 'approved';
    const isRejected = ob?.status === 'rejected';
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA] p-4">
        <div className="max-w-md w-full card-swiss p-8 text-center" data-testid="onboarding-success">
          {isRejected ? (
            <>
              <Warning size={48} weight="thin" className="mx-auto text-[#E60000]" />
              <h2 className="text-xl font-bold text-[#111418] mt-3">Anfrage abgelehnt</h2>
              <p className="text-sm text-[#525866] mt-2">
                Diese Registrierungs-Anfrage wurde abgelehnt. Bitte kontaktieren Sie uns telefonisch, falls dies ein Irrtum ist.
              </p>
            </>
          ) : (
            <>
              <CheckCircle size={48} weight="thin" className="mx-auto text-[#009951]" />
              <h2 className="text-xl font-bold text-[#111418] mt-3">
                {isApproved ? 'Herzlich willkommen!' : 'Vielen Dank!'}
              </h2>
              <p className="text-sm text-[#525866] mt-2">
                {isApproved
                  ? 'Ihre Daten wurden bestätigt. Sie sind jetzt als Kunde registriert und können Fahrzeuge mieten.'
                  : 'Ihre Anfrage ist bei uns eingegangen und wird geprüft. Wir melden uns kurzfristig bei Ihnen.'}
              </p>
              <p className="text-xs text-[#8A94A6] mt-6">
                Referenz: <span className="font-mono">{ob?.id?.substring(0, 8).toUpperCase()}</span>
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  const current = STEPS[step];

  const goNext = async () => {
    // Simple client-side validation per step
    if (current.key === 'type') {
      if (!form.customer_type) {
        toast.error('Bitte Kundentyp wählen');
        return;
      }
    } else if (current.key === 'personal') {
      if (!form.first_name || !form.last_name || !form.date_of_birth) {
        toast.error('Bitte Vorname, Nachname und Geburtsdatum ausfüllen');
        return;
      }
      if (form.customer_type === 'business') {
        if (!form.company_name) {
          toast.error('Bitte Firmenname eintragen');
          return;
        }
        if (!form.tax_number && !form.vat_id) {
          toast.error('Bitte Steuernummer oder USt-IdNr. angeben');
          return;
        }
      }
    } else if (current.key === 'address') {
      if (!form.email || !form.phone || !form.address || !form.postal_code || !form.city) {
        toast.error('Bitte alle Kontakt- und Adressfelder ausfüllen');
        return;
      }
    } else if (current.key === 'documents') {
      if (!form.driver_license_number || !form.driver_license_expiry) {
        toast.error('Bitte Führerscheinnummer und Gültigkeit angeben');
        return;
      }
    } else if (current.key === 'uploads') {
      const docs = ob?.documents || {};
      const missing = DOC_TYPES.filter((d) => !docs[d.key]);
      if (missing.length > 0) {
        toast.error(`Bitte alle 4 Fotos hochladen. Fehlt: ${missing.map((d) => d.label).join(', ')}`);
        return;
      }
    }
    await saveForm({});
    setStep(step + 1);
  };

  const goBack = () => setStep(Math.max(0, step - 1));

  return (
    <div className="min-h-screen bg-[#F7F8FA] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-sm bg-[#E60000] flex items-center justify-center">
              <Car weight="bold" size={20} className="text-white" />
            </div>
            <span className="text-lg font-bold text-[#111418]">AutoVermietung</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#111418]">Kundenregistrierung</h1>
          <p className="text-sm text-[#525866] mt-1">Bitte tragen Sie Ihre Daten ein und laden Sie Ausweis + Führerschein hoch.</p>
        </div>

        {/* Progress bar */}
        <div className="card-swiss p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isDone = i < step;
              const isCurrent = i === step;
              return (
                <div key={s.key} className="flex-1 flex items-center" data-testid={`step-indicator-${s.key}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    isCurrent ? 'bg-[#E60000] text-white' : isDone ? 'bg-[#009951] text-white' : 'bg-[#F0F1F3] text-[#8A94A6]'
                  }`}>
                    {isDone ? <CheckCircle size={16} weight="bold" /> : <Icon size={16} weight="bold" />}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1 ${isDone ? 'bg-[#009951]' : 'bg-[#F0F1F3]'}`} />
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-center text-xs text-[#8A94A6]">Schritt {step + 1} von {STEPS.length}: {current.label}</p>
        </div>

        {/* Step Content */}
        <div className="card-swiss p-6" data-testid={`onboarding-step-${current.key}`}>
          {current.key === 'type' && (
            <div className="space-y-4">
              <p className="text-sm text-[#525866]">Sind Sie eine Privatperson oder registrieren Sie sich für Ihre Firma?</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: 'private', label: 'Privatperson', desc: 'Miete für private Zwecke' },
                  { v: 'business', label: 'Firma / Gewerbe', desc: 'Steuernr., USt-IdNr. usw.' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setForm({ ...form, customer_type: opt.v })}
                    className={`p-4 rounded-sm border-2 text-left transition-all ${
                      form.customer_type === opt.v
                        ? 'border-[#E60000] bg-[#FFF5F5]'
                        : 'border-[#E5E7EB] hover:border-[#C8CBD0]'
                    }`}
                    data-testid={`customer-type-${opt.v}`}
                  >
                    <p className="font-semibold text-[#111418]">{opt.label}</p>
                    <p className="text-xs text-[#525866] mt-1">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {current.key === 'personal' && (
            <div className="space-y-4">
              {form.customer_type === 'business' && (
                <div className="p-3 bg-[#FFF7E6] border border-[#F4D48A] rounded-sm text-xs text-[#B8860B]">
                  Firmen-Registrierung — bitte zuerst die Firmendaten, dann die Daten des Ansprechpartners (Fahrer).
                </div>
              )}

              {form.customer_type === 'business' && (
                <>
                  <p className="text-sm font-semibold text-[#111418]">Firmendaten</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <Label>Firmenname *</Label>
                      <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="input-company-name" />
                    </div>
                    <div>
                      <Label>Rechtsform</Label>
                      <Select value={form.company_legal_form} onValueChange={(v) => setForm({ ...form, company_legal_form: v })}>
                        <SelectTrigger data-testid="input-legal-form"><SelectValue placeholder="z.B. GmbH" /></SelectTrigger>
                        <SelectContent>
                          {LEGAL_FORMS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Steuernummer *</Label>
                      <Input value={form.tax_number} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} placeholder="z.B. 12/345/67890" data-testid="input-tax-number" />
                    </div>
                    <div>
                      <Label>USt-IdNr. *</Label>
                      <Input value={form.vat_id} onChange={(e) => setForm({ ...form, vat_id: e.target.value })} placeholder="z.B. DE123456789" data-testid="input-vat-id" />
                    </div>
                    <div>
                      <Label>Handelsregister-Nr.</Label>
                      <Input value={form.register_number} onChange={(e) => setForm({ ...form, register_number: e.target.value })} placeholder="z.B. HRB 12345" data-testid="input-register-number" />
                    </div>
                    <div className="col-span-2">
                      <Label>Registergericht</Label>
                      <Input value={form.register_court} onChange={(e) => setForm({ ...form, register_court: e.target.value })} placeholder="z.B. Amtsgericht Konstanz" data-testid="input-register-court" />
                    </div>
                  </div>
                  <p className="text-xs text-[#8A94A6]">* Steuernummer oder USt-IdNr. — eines der beiden Felder muss ausgefüllt sein.</p>
                  <div className="border-t border-[#E5E7EB] pt-4 mt-4">
                    <p className="text-sm font-semibold text-[#111418] mb-3">Ansprechpartner / Fahrer</p>
                  </div>
                </>
              )}

              <div>
                <Label>Anrede</Label>
                <Select value={form.salutation} onValueChange={(v) => setForm({ ...form, salutation: v })}>
                  <SelectTrigger data-testid="input-salutation"><SelectValue placeholder="Bitte wählen" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Herr">Herr</SelectItem>
                    <SelectItem value="Frau">Frau</SelectItem>
                    <SelectItem value="Divers">Divers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Vorname *</Label>
                  <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="input-first-name" />
                </div>
                <div>
                  <Label>Nachname *</Label>
                  <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="input-last-name" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Geburtsdatum *</Label>
                  <Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="input-dob" />
                </div>
                <div>
                  <Label>Geburtsort</Label>
                  <Input value={form.birth_place} onChange={(e) => setForm({ ...form, birth_place: e.target.value })} data-testid="input-birth-place" />
                </div>
              </div>
            </div>
          )}

          {current.key === 'address' && (
            <div className="space-y-4">
              <div>
                <Label>E-Mail *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-email" />
              </div>
              <div>
                <Label>Telefon *</Label>
                <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="input-phone" />
              </div>
              <div>
                <Label>Straße und Hausnummer *</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="input-address" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>PLZ *</Label>
                  <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="input-plz" />
                </div>
                <div className="col-span-2">
                  <Label>Ort *</Label>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="input-city" />
                </div>
              </div>
              <div>
                <Label>Land</Label>
                <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="input-country" />
              </div>
            </div>
          )}

          {current.key === 'documents' && (
            <div className="space-y-4">
              <div className="p-3 bg-[#F7F8FA] border border-[#E5E7EB] rounded-sm text-xs text-[#525866]">
                Ausweisnummer ist optional, Führerscheindaten sind Pflicht.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ausweisnummer</Label>
                  <Input value={form.id_card_number} onChange={(e) => setForm({ ...form, id_card_number: e.target.value })} data-testid="input-id-number" />
                </div>
                <div>
                  <Label>Ausweis gültig bis</Label>
                  <Input type="date" value={form.id_card_expiry} onChange={(e) => setForm({ ...form, id_card_expiry: e.target.value })} data-testid="input-id-expiry" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Führerscheinnummer *</Label>
                  <Input value={form.driver_license_number} onChange={(e) => setForm({ ...form, driver_license_number: e.target.value })} data-testid="input-dl-number" />
                </div>
                <div>
                  <Label>Führerschein gültig bis *</Label>
                  <Input type="date" value={form.driver_license_expiry} onChange={(e) => setForm({ ...form, driver_license_expiry: e.target.value })} data-testid="input-dl-expiry" />
                </div>
              </div>
              <div>
                <Label>Ausstellende Behörde</Label>
                <Input value={form.driver_license_authority} onChange={(e) => setForm({ ...form, driver_license_authority: e.target.value })} data-testid="input-dl-authority" />
              </div>
              <div>
                <Label>Führerscheinklassen</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {LICENSE_CLASSES.map((c) => {
                    const active = (form.driver_license_classes || []).includes(c);
                    return (
                      <button
                        key={c}
                        type="button"
                        className={`px-2.5 py-1 text-xs rounded-sm border ${
                          active ? 'bg-[#E60000] text-white border-[#E60000]' : 'bg-white text-[#525866] border-[#E5E7EB]'
                        }`}
                        onClick={() => {
                          const cur = form.driver_license_classes || [];
                          setForm({
                            ...form,
                            driver_license_classes: active ? cur.filter((x) => x !== c) : [...cur, c],
                          });
                        }}
                        data-testid={`license-class-${c}`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {current.key === 'uploads' && (
            <div className="space-y-4">
              <div className="p-3 bg-[#F7F8FA] border border-[#E5E7EB] rounded-sm text-xs text-[#525866]">
                Bitte laden Sie <strong>4 Fotos</strong> hoch (JPG / PNG / PDF, max. 15 MB pro Datei).
                Achten Sie auf gute Lesbarkeit ohne Reflexionen.
              </div>
              {DOC_TYPES.map((d) => {
                const hasDoc = !!(ob?.documents || {})[d.key];
                return (
                  <div key={d.key} className="border border-[#E5E7EB] rounded-sm p-3" data-testid={`upload-tile-${d.key}`}>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <p className="font-semibold text-[#111418] text-sm">{d.label}</p>
                      {hasDoc && (
                        <span className="text-xs text-[#009951] font-medium flex items-center gap-1">
                          <CheckCircle size={12} weight="bold" /> hochgeladen
                        </span>
                      )}
                    </div>
                    {hasDoc && (
                      <img
                        src={publicOnboardingApi.docUrl(token, d.key)}
                        alt={d.label}
                        className="w-full max-h-48 object-contain bg-[#F7F8FA] rounded-sm mb-2"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                    <div className="flex flex-wrap gap-2">
                      <input
                        ref={(r) => (inputRefs.current[d.key] = r)}
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        onChange={(e) => doUpload(d.key, e.target.files?.[0])}
                        data-testid={`file-input-${d.key}`}
                      />
                      <input
                        ref={(r) => (cameraRefs.current[d.key] = r)}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => doUpload(d.key, e.target.files?.[0])}
                        data-testid={`camera-input-${d.key}`}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => cameraRefs.current[d.key]?.click()}
                        disabled={uploading[d.key]}
                        data-testid={`camera-btn-${d.key}`}
                      >
                        <Camera size={14} weight="bold" className="mr-1.5" />
                        Foto
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => inputRefs.current[d.key]?.click()}
                        disabled={uploading[d.key]}
                        data-testid={`upload-btn-${d.key}`}
                      >
                        <Upload size={14} weight="bold" className="mr-1.5" />
                        {uploading[d.key] ? 'Wird hochgeladen…' : (hasDoc ? 'Ersetzen' : 'Datei wählen')}
                      </Button>
                      {hasDoc && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteDoc(d.key)}
                          className="text-[#E60000]"
                          data-testid={`delete-btn-${d.key}`}
                        >
                          <Trash size={14} weight="bold" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {current.key === 'review' && (
            <div className="space-y-4">
              <p className="text-sm text-[#525866]">
                Bitte prüfen Sie Ihre Angaben. Wenn alles stimmt, klicken Sie auf <strong>&bdquo;Anfrage absenden&ldquo;</strong>.
              </p>
              <div className="p-4 bg-[#F7F8FA] rounded-sm space-y-2 text-sm">
                <p><strong>Kundentyp:</strong> {form.customer_type === 'business' ? 'Firma / Gewerbe' : 'Privatperson'}</p>
                {form.customer_type === 'business' && (
                  <>
                    <p><strong>Firma:</strong> {form.company_name} {form.company_legal_form && `(${form.company_legal_form})`}</p>
                    {form.tax_number && <p><strong>Steuernr.:</strong> {form.tax_number}</p>}
                    {form.vat_id && <p><strong>USt-IdNr.:</strong> {form.vat_id}</p>}
                    {form.register_number && (
                      <p><strong>Handelsregister:</strong> {form.register_number}
                        {form.register_court && ` · ${form.register_court}`}
                      </p>
                    )}
                    <p className="text-xs text-[#8A94A6]">— Ansprechpartner / Fahrer —</p>
                  </>
                )}
                <p><strong>Name:</strong> {form.salutation} {form.first_name} {form.last_name}</p>
                <p><strong>Geboren:</strong> {form.date_of_birth} {form.birth_place && `in ${form.birth_place}`}</p>
                <p><strong>Adresse:</strong> {form.address}, {form.postal_code} {form.city}, {form.country}</p>
                <p><strong>Kontakt:</strong> {form.email} · {form.phone}</p>
                <p><strong>Führerschein:</strong> {form.driver_license_number} · gültig bis {form.driver_license_expiry}
                  {form.driver_license_authority && ` · ${form.driver_license_authority}`}
                  {(form.driver_license_classes || []).length > 0 && ` · Klassen: ${form.driver_license_classes.join(', ')}`}
                </p>
                <p><strong>Fotos:</strong> {Object.keys(ob?.documents || {}).length} / 4 hochgeladen</p>
              </div>
              <div>
                <Label>Anmerkungen (optional)</Label>
                <textarea
                  className="w-full mt-1 min-h-[80px] border border-[#E5E7EB] rounded-sm px-3 py-2 text-sm"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="z.B. Zusatzfahrer, Wunschtermin, etc."
                  data-testid="input-notes"
                />
              </div>
              <div className="p-3 bg-[#FFF7E6] border border-[#F4D48A] rounded-sm text-xs text-[#B8860B]">
                Mit dem Absenden bestätigen Sie, dass alle Angaben korrekt sind und die hochgeladenen Fotos aktuell sowie unverändert sind.
              </div>
            </div>
          )}

          <div className="flex justify-between items-center mt-6 pt-4 border-t border-[#E5E7EB]">
            <Button
              variant="ghost"
              onClick={goBack}
              disabled={step === 0 || saving}
              data-testid="btn-back"
            >
              <ArrowLeft size={14} weight="bold" className="mr-1.5" />
              Zurück
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                onClick={goNext}
                disabled={saving}
                className="btn-primary"
                data-testid="btn-next"
              >
                Weiter
                <ArrowRight size={14} weight="bold" className="ml-1.5" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={submitting}
                className="btn-primary"
                data-testid="btn-submit"
              >
                {submitting ? 'Wird gesendet…' : 'Anfrage absenden'}
                <CheckCircle size={14} weight="bold" className="ml-1.5" />
              </Button>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-[#8A94A6] mt-4">
          Ihre Daten werden verschlüsselt übertragen und nur intern verarbeitet.
        </p>
      </div>
    </div>
  );
};

export default PublicOnboarding;
